import * as _ from 'lodash-es';
import moment from 'moment';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import getFeature from '../../utils/features';

const HISTORY_FRAGMENT = gql(`
  fragment partitionAndGroupHistories_history on SlimHistory {
    id
    insertedAt
    versionFrom
    versionTo
    authors {
      id
    }
  }
`);

const GROUP_INTERVAL_IN_HOURS = 2;

type History = DocumentType<typeof HISTORY_FRAGMENT>;

export type HistoryGroup<T extends History> = {
  histories: T[];
  versionFrom: number;
  versionTo: number;
  authors: T['authors'];
  insertedAt: string;
  isAccessible: boolean;
};

const groupHistories = <T extends History>(
  histories: T[],
  isAccessible: boolean,
): HistoryGroup<T>[] => {
  const sortedHistories = histories.toSorted(
    (a, b) =>
      new Date(b.insertedAt).valueOf() - new Date(a.insertedAt).valueOf(),
  );

  const groupedHistories: HistoryGroup<T>[] = [];
  let currentGroup: HistoryGroup<T> | null = null;
  let lastInsertedAt: number | null = null;

  for (const history of sortedHistories) {
    const currentInsertedAt = new Date(history.insertedAt).valueOf();

    const differenceInHours = lastInsertedAt
      ? (lastInsertedAt - currentInsertedAt) / (1000 * 60 * 60)
      : 0;

    if (differenceInHours > GROUP_INTERVAL_IN_HOURS && currentGroup) {
      groupedHistories.push(currentGroup);
      currentGroup = null;
    }

    if (currentGroup == null) {
      currentGroup = {
        histories: [history],
        versionFrom: history.versionFrom,
        versionTo: history.versionTo,
        authors: history.authors,
        insertedAt: history.insertedAt,
        isAccessible,
      };
    } else {
      currentGroup.histories.push(history);
      currentGroup.versionFrom = Math.min(
        currentGroup.versionFrom,
        history.versionFrom,
      );
      currentGroup.versionTo = Math.max(
        currentGroup.versionTo,
        history.versionTo,
      );
      currentGroup.authors = _.uniqBy(
        [...currentGroup.authors, ...history.authors],
        'id',
      );
    }

    lastInsertedAt = currentInsertedAt;
  }

  if (currentGroup) {
    groupedHistories.push(currentGroup);
  }

  return groupedHistories;
};

const partitionAndGroupHistories = <T extends History>(
  histories: T[],
  versionHistoryRestrictionDays = getFeature('version_history'),
) => {
  const historiesWithAuthors = histories.filter(
    ({ authors }) => Object.keys(authors).length > 0,
  );

  const [accessibleHistories, restrictedHistories] = _.partition(
    historiesWithAuthors,
    (history) => {
      if (versionHistoryRestrictionDays === Infinity) {
        return true;
      }

      const restrictedAt = moment().subtract(
        versionHistoryRestrictionDays + 1,
        'days',
      );

      return new Date(history.insertedAt) > restrictedAt.toDate();
    },
  );

  return {
    accessibleHistories: groupHistories(accessibleHistories, true),
    restrictedHistories: groupHistories(restrictedHistories, false),
  };
};

export default partitionAndGroupHistories;
